import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { AgMediaUploaderModalService } from './ag-media-uploader-modal.service';
import { Observable, firstValueFrom } from 'rxjs';
import { ModalWindowComponent } from '../modal-window/modal-window.component';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AGMedia } from '../../../../../../../ag-common-lib/projects/ag-common-lib/src/lib/models/utils/ag-media.model';
import {
  DEFAULT_MAX_MEDIA_UPLOAD_SIZE_MB,
  FILE_NAME_VALIDATION_GROUP,
  MediaUploaderTabs,
} from './ag-media-uploader-modal.models';
import { MediaSourcesComponent } from './media-sources/media-sources.component';
import { confirm } from 'devextreme/ui/dialog';
import { resetDxGroupsValidation } from 'ag-common-svc/lib/utils/validation';

@UntilDestroy()
@Component({
  selector: 'ag-shr-media-uploader-modal',
  templateUrl: './ag-media-uploader-modal.component.html',
  styleUrls: ['./ag-media-uploader-modal.component.scss'],
  providers: [AgMediaUploaderModalService],
})
export class AgMediaUploaderModalComponent {
  @Input() accept = ['image/*'];
  @Input() mediaPathPrefix;
  @Input() mediaSources: MediaUploaderTabs[];
  @Input() mediaItems: AGMedia[];
  @Input() placeholder: string;
  @Input() width: string | number = '680px';
  @Input() height: string | number = '700px';
  @Input() useCustomMediaGallery: boolean = false;
  @Input() maxFileSizeMb = DEFAULT_MAX_MEDIA_UPLOAD_SIZE_MB;
  @Input() applyImageDownscale = false;
  @Input() set waitForProcessing(value: boolean) {
    this.mediaUploaderService.setWaitForProcessing(!!value);
  }

  @Output() mediaChanged = new EventEmitter<AGMedia>();

  @ViewChild('mediaUploaderModalRef', { static: true }) mediaUploaderModalComponent: ModalWindowComponent;
  @ViewChild('mediaSourcesRef', { static: false }) mediaSourcesComponent: MediaSourcesComponent;

  protected inProgress$: Observable<boolean> = this.mediaUploaderService.inProgress$;
  protected isFileSelected$: Observable<boolean> = this.mediaUploaderService.isFileSelected$;
  protected uploadProgress$: Observable<number> = this.mediaUploaderService.uploadProgress$;

  onCloseModal;

  constructor(private mediaUploaderService: AgMediaUploaderModalService) {
    this.onCloseModal = this.handleModalClose.bind(this);
  }

  async saveImage() {
    try {
      const media = await this.mediaUploaderService.handleSave();
      this.mediaChanged.emit(media);
      this.mediaUploaderService.setSelectedFromGallery(null);
      this.mediaUploaderService.setFile(null);
      this.mediaUploaderModalComponent.hideModal();
    } catch (error) {}
  }

  onAfterRevertChanges = (): void => {};

  async handleModalClose(): Promise<boolean> {
    const inLoading = this.mediaSourcesComponent?.uploadFromUrlTabComponent?.isLoadingSig();
    if (inLoading) {
      const confirmationMessage = `<div>Image load is in progress. Are you sure want to stop it?</div>`;
      const isConfirmed = await confirm(confirmationMessage, 'Confirm');
      if (!isConfirmed) {
        return false;
      }
    }

    const isFileSelected = await firstValueFrom(this.isFileSelected$).catch(() => false);
    if (isFileSelected) {
      const confirmationMessage =
        '<i>You are have not saved data. <br/> Are you sure you want to close without Saving?</i>';
      const isConfirmed = await confirm(confirmationMessage, 'Confirm');
      if (!isConfirmed) {
        return false;
      }
    }

    await this.mediaUploaderService.onCancelEdit(this.onAfterRevertChanges);
    this.resetUiState();
    return true;
  }

  showModal() {
    this.mediaUploaderService.resetState();
    this.resetUiState();
    resetDxGroupsValidation(FILE_NAME_VALIDATION_GROUP);
    this.mediaUploaderModalComponent.showModal();
  }

  private resetUiState() {
    this.mediaSourcesComponent?.uploadFromDiskTabComponent?.clearFile();
    this.mediaSourcesComponent?.uploadFromUrlTabComponent?.reset();
  }
}
